/**
 * Currency Role - Constants
 *
 * Códigos de moeda, símbolos e configurações de formatação.
 *
 * Currency é sobre VALOR MONETÁRIO, não sobre tipos de moeda.
 * O código da moeda (BRL, USD, etc) é METADATA usado apenas para formatação.
 *
 * NÃO há conversão entre moedas - isso é operação de negócio com taxas externas.
 */

// =============================================================================
// TYPES
// =============================================================================

/**
 * Códigos de moeda ISO 4217
 */
export type CurrencyCode =
  // Major currencies
  | "USD"
  | "EUR"
  | "GBP"
  | "JPY"
  | "CHF"
  | "CNY"
  | "CAD"
  | "AUD"
  // Latin America
  | "BRL"
  | "MXN"
  | "ARS"
  | "CLP"
  | "COP"
  // Other important
  | "INR"
  | "KRW"
  | "RUB"
  | "TRY"
  | "ZAR";

/**
 * Configuração de uma moeda para formatação
 */
export interface CurrencyConfig {
  /** Código ISO 4217 */
  code: CurrencyCode;
  /** Símbolo da moeda */
  symbol: string;
  /** Nome singular */
  singular: string;
  /** Nome plural */
  plural: string;
  /** Casas decimais padrão */
  decimals: number;
  /** Símbolo vem antes do valor? */
  symbolFirst: boolean;
  /** Separador de milhares */
  thousandsSeparator: string;
  /** Separador decimal */
  decimalSeparator: string;
}

// =============================================================================
// CURRENCY CONFIGS
// =============================================================================

/**
 * Configuração de cada moeda para formatação
 */
export const CURRENCY_CONFIGS: Record<CurrencyCode, CurrencyConfig> = {
  // ===========================================================================
  // Major Currencies
  // ===========================================================================
  USD: {
    code: "USD",
    symbol: "$",
    singular: "US dollar",
    plural: "US dollars",
    decimals: 2,
    symbolFirst: true,
    thousandsSeparator: ",",
    decimalSeparator: ".",
  },
  EUR: {
    code: "EUR",
    symbol: "€",
    singular: "euro",
    plural: "euros",
    decimals: 2,
    symbolFirst: false, // 100 € (europeu) ou €100 (varia)
    thousandsSeparator: ".",
    decimalSeparator: ",",
  },
  GBP: {
    code: "GBP",
    symbol: "£",
    singular: "British pound",
    plural: "British pounds",
    decimals: 2,
    symbolFirst: true,
    thousandsSeparator: ",",
    decimalSeparator: ".",
  },
  JPY: {
    code: "JPY",
    symbol: "¥",
    singular: "Japanese yen",
    plural: "Japanese yen",
    decimals: 0, // Yen não tem centavos
    symbolFirst: true,
    thousandsSeparator: ",",
    decimalSeparator: ".",
  },
  CHF: {
    code: "CHF",
    symbol: "CHF",
    singular: "Swiss franc",
    plural: "Swiss francs",
    decimals: 2,
    symbolFirst: true,
    thousandsSeparator: "'",
    decimalSeparator: ".",
  },
  CNY: {
    code: "CNY",
    symbol: "¥",
    singular: "Chinese yuan",
    plural: "Chinese yuan",
    decimals: 2,
    symbolFirst: true,
    thousandsSeparator: ",",
    decimalSeparator: ".",
  },
  CAD: {
    code: "CAD",
    symbol: "C$",
    singular: "Canadian dollar",
    plural: "Canadian dollars",
    decimals: 2,
    symbolFirst: true,
    thousandsSeparator: ",",
    decimalSeparator: ".",
  },
  AUD: {
    code: "AUD",
    symbol: "A$",
    singular: "Australian dollar",
    plural: "Australian dollars",
    decimals: 2,
    symbolFirst: true,
    thousandsSeparator: ",",
    decimalSeparator: ".",
  },

  // ===========================================================================
  // Latin America
  // ===========================================================================
  BRL: {
    code: "BRL",
    symbol: "R$",
    singular: "Brazilian real",
    plural: "Brazilian reais",
    decimals: 2,
    symbolFirst: true,
    thousandsSeparator: ".",
    decimalSeparator: ",",
  },
  MXN: {
    code: "MXN",
    symbol: "$",
    singular: "Mexican peso",
    plural: "Mexican pesos",
    decimals: 2,
    symbolFirst: true,
    thousandsSeparator: ",",
    decimalSeparator: ".",
  },
  ARS: {
    code: "ARS",
    symbol: "$",
    singular: "Argentine peso",
    plural: "Argentine pesos",
    decimals: 2,
    symbolFirst: true,
    thousandsSeparator: ".",
    decimalSeparator: ",",
  },
  CLP: {
    code: "CLP",
    symbol: "$",
    singular: "Chilean peso",
    plural: "Chilean pesos",
    decimals: 0, // Peso chileno não tem centavos
    symbolFirst: true,
    thousandsSeparator: ".",
    decimalSeparator: ",",
  },
  COP: {
    code: "COP",
    symbol: "$",
    singular: "Colombian peso",
    plural: "Colombian pesos",
    decimals: 0, // Peso colombiano praticamente não usa centavos
    symbolFirst: true,
    thousandsSeparator: ".",
    decimalSeparator: ",",
  },

  // ===========================================================================
  // Other Important
  // ===========================================================================
  INR: {
    code: "INR",
    symbol: "₹",
    singular: "Indian rupee",
    plural: "Indian rupees",
    decimals: 2,
    symbolFirst: true,
    thousandsSeparator: ",",
    decimalSeparator: ".",
  },
  KRW: {
    code: "KRW",
    symbol: "₩",
    singular: "South Korean won",
    plural: "South Korean won",
    decimals: 0, // Won não tem centavos
    symbolFirst: true,
    thousandsSeparator: ",",
    decimalSeparator: ".",
  },
  RUB: {
    code: "RUB",
    symbol: "₽",
    singular: "Russian ruble",
    plural: "Russian rubles",
    decimals: 2,
    symbolFirst: false,
    thousandsSeparator: " ",
    decimalSeparator: ",",
  },
  TRY: {
    code: "TRY",
    symbol: "₺",
    singular: "Turkish lira",
    plural: "Turkish lira",
    decimals: 2,
    symbolFirst: true,
    thousandsSeparator: ".",
    decimalSeparator: ",",
  },
  ZAR: {
    code: "ZAR",
    symbol: "R",
    singular: "South African rand",
    plural: "South African rand",
    decimals: 2,
    symbolFirst: true,
    thousandsSeparator: " ",
    decimalSeparator: ",",
  },
};

// =============================================================================
// SYMBOL TO CODE MAPPING
// =============================================================================

/**
 * Mapeamento de símbolos para códigos de moeda
 * Usado no cast para detectar moeda a partir de string
 *
 * NOTA: Alguns símbolos são ambíguos ($ é usado por várias moedas)
 * O cast retorna apenas o valor numérico, não infere a moeda
 */
export const CURRENCY_SYMBOLS: Record<string, CurrencyCode[]> = {
  $: ["USD", "CAD", "AUD", "MXN", "ARS", "CLP", "COP"],
  "€": ["EUR"],
  "£": ["GBP"],
  "¥": ["JPY", "CNY"],
  "R$": ["BRL"],
  "C$": ["CAD"],
  "A$": ["AUD"],
  CHF: ["CHF"],
  "₹": ["INR"],
  "₩": ["KRW"],
  "₽": ["RUB"],
  "₺": ["TRY"],
  R: ["ZAR"],
};

// =============================================================================
// HELPERS
// =============================================================================

/** Lista de todos os códigos de moeda */
export const CURRENCY_CODE_LIST = Object.keys(CURRENCY_CONFIGS) as CurrencyCode[];

/**
 * Retorna configuração de uma moeda
 */
export function getCurrencyConfig(code: CurrencyCode): CurrencyConfig {
  return CURRENCY_CONFIGS[code];
}

/**
 * Verifica se é um código de moeda válido
 */
export function isCurrencyCode(code: string): code is CurrencyCode {
  return code.toUpperCase() in CURRENCY_CONFIGS;
}
